'use client';

import { useAffiliateLink } from '@hooks/use-affiliate-link';
import { useBetterMediaQuery } from '@hooks/use-ssr-media-query';
import { trackGa4ItemListView, trackGa4ItemSelection } from '@v2/analytics/ga4';
import { useHover } from '@uidotdev/usehooks';
import {
  getInternationalFullDate,
  getInternationalTime,
} from '@utils/native-dates';
import { v2Links } from '@utils/navigation/links';
import * as EventsSelectionList from '@v2/containers/EventsSelectionList/EventsSelectionList';
import { EventResponse } from '@v2/types';
import { ChevronRight } from '@v2/ui/Icon/Arrow';
import { cn } from '@v2/utils/cn';
import { format, parseISO } from 'date-fns';
import Link from 'next/link';
import { useEffect, useMemo, useRef } from 'react';
import { shouldHideLeagueEvent } from '../../util';
import { EventResultsUiLayout } from '../../constants/eventPageUiTypes';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const getRemainingDays = (occursAt: string) => {
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const eventStart = new Date(`${occursAt.split('T')[0]}T00:00:00`);

  return Math.round((eventStart.getTime() - todayStart.getTime()) / DAY_IN_MS);
};

interface Props {
  title?: string;
  headerText?: string;
  identifier: string;
  events: EventResponse[];
  className?: string;
  shouldReturnEmpty?: boolean;
  actionLabel?: string;
  layout?: EventResultsUiLayout;
  itemListName?: string;
  itemCategory?: string;
}

export const EventsList = ({
  title,
  headerText,
  events,
  identifier,
  className,
  shouldReturnEmpty = false,
  actionLabel = 'Build package',
  layout = 'default',
  itemListName = `event_list_${identifier}`,
  itemCategory,
}: Props) => {
  const trackedListKeys = useRef(new Set<string>());
  const filteredEvents = useMemo(
    () => events?.filter((ev) => !shouldHideLeagueEvent(ev.name)) ?? [],
    [events]
  );
  const itemListId = itemListName;
  const listKey = filteredEvents
    .map((event) => `${event.id}:${event.name}`)
    .join('|');
  const trackingKey = `${itemListId}:${itemListName}:${itemCategory ?? ''}:${listKey}`;
  const isTeamSchedule = layout === 'teamSchedule';
  const ListWrapper = isTeamSchedule ? 'div' : EventsSelectionList.Wrapper;

  useEffect(() => {
    if (!filteredEvents.length || trackedListKeys.current.has(trackingKey))
      return;

    trackedListKeys.current.add(trackingKey);
    trackGa4ItemListView({
      itemListId,
      itemListName,
      items: filteredEvents.map((event, index) => ({
        item_id: String(event.id),
        item_name: event.name,
        ...(itemCategory ? { item_category: itemCategory } : {}),
        index,
      })),
    });
  }, [filteredEvents, itemCategory, itemListId, itemListName, trackingKey]);

  if (!events && !shouldReturnEmpty) return <></>;

  // Show empty state when no events are available
  if (filteredEvents.length === 0) {
    return (
      <div className={cn(className)}>
        {title && <h2 className="mb-6 text-2xl font-bold">{title}</h2>}
        {headerText &&
          (isTeamSchedule ? (
            <p
              aria-live="polite"
              className="border-b border-gray-200 py-3 text-sm leading-5 text-gray-600"
            >
              {headerText}
            </p>
          ) : (
            <EventsSelectionList.Header>
              {headerText}
            </EventsSelectionList.Header>
          ))}
      </div>
    );
  }

  return (
    <div className={cn(className)}>
      {title && <h2 className="mb-6 text-2xl font-bold">{title}</h2>}
      {headerText &&
        (isTeamSchedule ? (
          <p className="sr-only" aria-live="polite">
            {headerText}
          </p>
        ) : (
          <EventsSelectionList.Header>{headerText}</EventsSelectionList.Header>
        ))}
      <ListWrapper
        {...(isTeamSchedule
          ? { className: 'mt-4 flex flex-col gap-6 md:mt-0' }
          : {})}
      >
        {filteredEvents?.map((event, index) => (
          <EventCard
            key={`${identifier}-${event.id}-${event.name}`}
            event={event}
            identifier={identifier}
            actionLabel={actionLabel}
            layout={layout}
            itemListId={itemListId}
            itemListName={itemListName}
            itemCategory={itemCategory}
            index={index}
          />
        ))}
      </ListWrapper>
    </div>
  );
};

interface EventCardProps {
  event: EventResponse;
  identifier: string;
  actionLabel: string;
  layout: EventResultsUiLayout;
  itemListId: string;
  itemListName: string;
  itemCategory?: string;
  index: number;
}

export const EventCard = ({
  event,
  identifier,
  actionLabel,
  layout,
  itemListId,
  itemListName,
  itemCategory,
  index,
}: EventCardProps) => {
  const [ref, hovering] = useHover();

  const { link } = useAffiliateLink();

  const isTablet = useBetterMediaQuery('(max-width: 768px)');

  const dateFull = getInternationalFullDate(
    event.occurs_at_local,
    undefined
  ).split(', ');

  const day = dateFull[0];
  const date = dateFull[1];
  const time = getInternationalTime(event.occurs_at_local, true);
  const eventLink = event.name
    .concat(' ', day, ' ', date)
    .replaceAll(' ', '-')
    .replaceAll('.', '');

  const isVenueUnderEventLayout = layout === 'venueUnderEvent';
  const isTeamScheduleLayout = layout === 'teamSchedule';
  const hasMobileCopyLink = Boolean(link && isTablet);
  const venueLocation = event?.venue.location || event?.venue.address?.location;
  const venueSummary = [event?.venue.name, venueLocation]
    .filter(Boolean)
    .join(' - ');
  const remainingDaysForTheEvent = getRemainingDays(event.occurs_at_local);
  const eventDescription = isVenueUnderEventLayout ? (
    <span>{venueSummary}</span>
  ) : remainingDaysForTheEvent < 2 ? (
    ['Today', 'Tomorrow'][remainingDaysForTheEvent]
  ) : (
    `In ${remainingDaysForTheEvent} days`
  );
  const localEventDate = parseISO(event.occurs_at_local.slice(0, 10));
  const hasValidLocalEventDate = Number.isFinite(localEventDate.getTime());
  const trackSelection = () =>
    trackGa4ItemSelection({
      itemListId,
      itemListName,
      item: {
        item_id: String(event.id),
        item_name: event.name,
        ...(itemCategory ? { item_category: itemCategory } : {}),
        index,
      },
    });

  return (
    <div ref={ref} key={`${identifier}-${event.id}-${event.name}`}>
      <Link
        prefetch={false}
        target="_blank"
        href={`${v2Links.buy}?eventId=${event.id}&eventLink=${eventLink}`}
        onClick={trackSelection}
        className={isTeamScheduleLayout ? 'block text-left' : undefined}
      >
        {isTeamScheduleLayout ? (
          <div className="grid min-h-20 grid-cols-[80px_minmax(0,1fr)_auto] items-center gap-3 md:gap-4">
            <time
              dateTime={event.occurs_at_local}
              className="flex size-20 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-center text-gray-800"
            >
              <span className="flex min-h-0 flex-1 flex-col items-center justify-center gap-0.5">
                <span className="text-sm font-semibold uppercase leading-4">
                  {hasValidLocalEventDate
                    ? format(localEventDate, 'MMM').toUpperCase()
                    : 'Date'}
                </span>
                <span className="text-2xl font-bold leading-6">
                  {hasValidLocalEventDate ? format(localEventDate, 'dd') : '--'}
                </span>
              </span>
              <span className="flex h-6 w-full shrink-0 items-center justify-center bg-gray-100 text-xs font-semibold uppercase leading-4 text-gray-600">
                {hasValidLocalEventDate
                  ? format(localEventDate, 'EEE').toUpperCase()
                  : ' '}
              </span>
            </time>
            <div className="min-w-0">
              <p
                title={event.name}
                className="line-clamp-2 break-words text-sm font-semibold leading-6 text-gray-800 md:line-clamp-1 md:text-base"
              >
                {event.name}
              </p>
              <p className="mt-1 text-sm font-medium leading-5 text-gray-600 md:truncate">
                <span>{time}</span>
                <span className="hidden md:inline"> · </span>
                <span className="block truncate md:inline">
                  {[event.venue?.name, venueLocation]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              </p>
            </div>
            <div className="flex items-center justify-end gap-2">
              {(hasMobileCopyLink || (!isTablet && hovering)) && (
                <EventsSelectionList.CopyLink
                  triggerClassName="flex justify-end"
                  eventId={event.id}
                  eventLink={eventLink}
                />
              )}
              <span className="inline-flex min-h-10 max-w-24 items-center justify-center rounded-lg bg-primary-500 px-2 text-center text-xs font-semibold text-white md:max-w-none md:px-4 md:text-sm">
                {actionLabel}
              </span>
            </div>
          </div>
        ) : (
          <EventsSelectionList.Card>
            <EventsSelectionList.Row
              className="col-span-2 md:col-span-1"
              title={date}
              description={time}
            />
            <EventsSelectionList.Row
              className={cn(
                isVenueUnderEventLayout
                  ? hasMobileCopyLink
                    ? 'col-span-5 md:col-span-7'
                    : 'col-span-6 md:col-span-7'
                  : 'col-span-5 md:col-span-5',
                !link && !isVenueUnderEventLayout && 'col-span-6'
              )}
              title={event.name}
              description={eventDescription}
            />

            {hasMobileCopyLink && (
              <div className="col-span-1 flex items-start justify-end">
                <EventsSelectionList.CopyLink
                  triggerClassName="flex justify-end "
                  eventId={event.id}
                  eventLink={eventLink}
                />
              </div>
            )}

            {!isVenueUnderEventLayout ? (
              <EventsSelectionList.Row
                className="hidden md:col-span-2 md:flex md:flex-col"
                title={event?.venue.name}
                description={venueLocation}
              />
            ) : null}

            <EventsSelectionList.Action className="hidden items-center justify-end md:col-span-2 md:flex">
              {!isTablet && hovering ? (
                <EventsSelectionList.CopyLink
                  eventId={event.id}
                  eventLink={eventLink}
                />
              ) : (
                <div />
              )}
              <div
                className={cn(
                  'flex flex-shrink-0 items-center font-semibold text-primary-500',
                  link && 'ml-5'
                )}
              >
                {actionLabel}{' '}
                <ChevronRight className="ml-2" size="16" stroke="#FA2846" />
              </div>
            </EventsSelectionList.Action>
          </EventsSelectionList.Card>
        )}
      </Link>
    </div>
  );
};
